/**
 * PostHog, loaded off the critical path (browser only).
 *
 * posthog-js is reachable ONLY through the dynamic import in loadPosthog(): it never enters a page's
 * initial bundle. PostHogLoader imports this module after window `load`, inside requestIdleCallback
 * (see PostHogLoader for its timing), and calls startPosthog(). Everything here is fire-and-forget: no function throws, returns a promise or
 * waits on PostHog, so a capture can never block or break the setup / payment flow. Captures made
 * before the library is ready are queued (capped) and flushed once it is.
 *
 * Identity: the first-party `_lmid` cookie is the anonymous id. It is passed to PostHog as
 * bootstrap.distinctID, so the id sent with setup-session calls before PostHog loads and PostHog's
 * own distinct id agree.
 *
 * Skipped entirely on the demo host and in the admin area; local dev only with ?qa=1 in the URL.
 */
import type { PostHog } from "posthog-js"

// Public client key (it ships in every page that loads PostHog).
const PH_TOKEN = "phc_w8jiUgnse2pJQ9WvcSQ3QEvZEhamGVzoAoXDdTnBYMoB"

const LMID_COOKIE = "_lmid"
const LANDING_KEY = "_lm_land"
const RECORDING_KEY = "_lm_rec"
const DEV_QA_KEY = "_lm_qa"
const ONCE_PREFIX = "_lm1:"
const MAX_QUEUE = 50
const ID_RE = /^[A-Za-z0-9_\-:.@$]{1,200}$/

/** Landing-URL params kept as session super properties (every utm_* is kept as well). */
const LANDING_PARAMS = new Set([
    "gclid", "gbraid", "wbraid", "dclid", "msclkid", "fbclid", "ttclid", "rdt_cid",
    "campaignid", "adgroupid", "keyword", "matchtype", "creative", "ad", "adid", "ad_id", "network", "device", "targetid",
    "msq", "loc_physical_ms", "utm_network", "utm_loc_physical_ms", "ref", "qa",
])

/**
 * Never stored or sent: sign-in / magic-link / OAuth secrets that ride on a landing URL (Supabase token_hash and code,
 * access/refresh/id tokens, signatures, one-time codes). The fragment is dropped too (implicit-flow tokens live there).
 */
const SECRET_PARAM_RE = /token|secret|passw|jwt|api_?key|signature|^(code|sig|otp|nonce|state|auth|session)$/i

/** The landing URL without its fragment and secret params (null when it does not parse). */
export function cleanLandingUrl(href: string): URL | null {
    try {
        const url = new URL(href)
        url.hash = ""
        for (const key of [...new Set(url.searchParams.keys())]) if (SECRET_PARAM_RE.test(key)) url.searchParams.delete(key)
        return url
    } catch {
        return null
    }
}

/** Never recorded: password fields, card fields (Evervault iframes / containers) and anything tagged ph-no-capture. */
const BLOCK_SELECTOR = [
    ".ph-no-capture",
    "input[type=\"password\"]",
    "input[autocomplete=\"current-password\"]",
    "input[autocomplete=\"new-password\"]",
    "input[autocomplete^=\"cc-\"]",
    "iframe[src*=\"evervault\"]",
    "[data-evervault]",
].join(", ")

const RECORDING_EVENTS = new Set(["checkout_started", "pay_pressed"])

/** Click ids only a paid-ad click puts on the landing URL. A session that landed with one is recorded (startPaidRecording). */
export const PAID_CLICK_IDS = ["gclid", "gbraid", "wbraid", "msclkid", "rdt_cid"] as const

/** The visitor's first real interaction: the touch beacon and a paid session's recorder wait for it (then an idle slot). */
const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"] as const
const LISTEN_OPTS: AddEventListenerOptions = { capture: true, passive: true }

const TOUCH_URL = "/api/t/touch"
/** Once-flag of a landing without ad/utm params; a landing with them gets its own flag (touchOnceKey). */
const TOUCH_ONCE = "touch"
/** requestIdleCallback fallback timeout, and the cap of the idle wait where rIC is missing. */
const IDLE_TIMEOUT_MS = 10_000
const TOUCH_MAX_PARAMS = 60

let ph: PostHog | null = null
let state: "idle" | "waiting" | "loading" | "ready" | "off" = "idle"
const queue: Array<(p: PostHog) => void> = []
const onceMemory = new Set<string>()
let lastIdentify = ""
let interacted = false
let interactionArmed = false
const afterInteraction: Array<() => void> = []

function readCookie(name: string): string | null {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string) {
    const secure = location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax${secure}`
}

function newId(): string {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
    } catch {
        /* fall through */
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

function excludedContext(): boolean {
    return location.hostname.split(".")[0] === "demo" || location.pathname.startsWith("/app/app_admin")
}

function devAllowed(): boolean {
    if (process.env.NODE_ENV === "production") return true
    try {
        if (new URLSearchParams(location.search).get("qa") === "1") sessionStorage.setItem(DEV_QA_KEY, "1")
        return sessionStorage.getItem(DEV_QA_KEY) === "1"
    } catch {
        return false
    }
}

/** First-party anonymous id (created on first use, kept a year). */
export function getLmid(): string | null {
    if (typeof document === "undefined") return null
    try {
        let id = readCookie(LMID_COOKIE)
        if (!id || !ID_RE.test(id)) {
            id = newId()
            writeCookie(LMID_COOKIE, id)
        }
        return id
    } catch {
        return null
    }
}

/** The id every setup-session call carries: PostHog's distinct id once loaded, else `_lmid`. */
export function getAnalyticsId(): string | null {
    try {
        const id = ph?.get_distinct_id()
        if (id && ID_RE.test(id)) return id
    } catch {
        /* fall through */
    }
    return getLmid()
}

/** Landing record of one URL: cleaned landing_url plus its ad/utm params (first value per lower-cased key). */
function landingRecord(href: string): Record<string, string> | null {
    const url = cleanLandingUrl(href)
    if (!url) return null
    const props: Record<string, string> = { landing_url: url.href.slice(0, 1000) }
    url.searchParams.forEach((value, rawKey) => {
        const key = rawKey.toLowerCase()
        if (value && !(key in props) && (key.startsWith("utm_") || LANDING_PARAMS.has(key))) props[key] = value.slice(0, 300)
    })
    return props
}

/** Canonical ad/utm attribution of a landing record ("" for an organic, param-less landing). */
function attributionOf(props: Record<string, unknown>): string {
    return Object.keys(props)
        .filter((k) => k !== "landing_url" && k !== "qa")
        .sort()
        .map((k) => `${k}=${String(props[k])}`)
        .join("&")
}

/** Short stable hash (djb2, base 36) for once-flag keys. */
function shortHash(s: string): string {
    let h = 5381
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0
    return (h >>> 0).toString(36)
}

/** Touch once-flag key: one per distinct ad/utm attribution in the tab, one shared key for param-less landings. */
export function touchOnceKey(landingHref: string): string {
    const record = landingRecord(landingHref)
    const attribution = record ? attributionOf(record) : ""
    return attribution ? `${TOUCH_ONCE}:${shortHash(attribution)}` : TOUCH_ONCE
}

/**
 * Stores the landing URL and its ad/utm params for this browser-tab session. First landing wins, except that a page
 * load with an explicit `href` carrying a different ad/utm attribution (an ad click after an organic visit in the same
 * tab) replaces it. The implicit call (no href: getLandingUrl) only fills a missing record.
 */
export function rememberLanding(href?: string): void {
    if (typeof window === "undefined") return
    try {
        const existing = sessionStorage.getItem(LANDING_KEY)
        if (existing && !href) return
        const props = landingRecord(href || location.href)
        if (!props) return
        if (existing) {
            const attribution = attributionOf(props)
            let previous = ""
            try {
                previous = attributionOf(JSON.parse(existing) || {})
            } catch {
                /* unreadable record: replace it */
            }
            if (!attribution || attribution === previous) return
        }
        sessionStorage.setItem(LANDING_KEY, JSON.stringify(props))
    } catch {
        /* storage unavailable: nothing to remember */
    }
}

/** Landing URL of this session (null when unknown). */
export function getLandingUrl(): string | null {
    if (typeof window === "undefined") return null
    try {
        rememberLanding()
        const raw = sessionStorage.getItem(LANDING_KEY)
        return raw ? (JSON.parse(raw).landing_url as string) || null : null
    } catch {
        return null
    }
}

/** True the first time `key` is seen in this tab session (sessionStorage, memory fallback). */
export function phOnce(key: string): boolean {
    const full = ONCE_PREFIX + key
    try {
        if (sessionStorage.getItem(full)) return false
        sessionStorage.setItem(full, "1")
        return true
    } catch {
        if (onceMemory.has(full)) return false
        onceMemory.add(full)
        return true
    }
}

function onceDone(key: string): boolean {
    const full = ONCE_PREFIX + key
    try {
        return sessionStorage.getItem(full) === "1" || onceMemory.has(full)
    } catch {
        return onceMemory.has(full)
    }
}

/**
 * Runs `fn` in a true idle slot: requestIdleCallback with a long (10 s) fallback timeout. Where rIC is missing
 * (Safari), a 50 ms probe timer stands in: `fn` runs once a probe fires on time (the main thread was free), at the
 * latest after 10 s.
 */
function whenIdle(fn: () => void) {
    const safe = () => {
        try {
            fn()
        } catch {
            /* never throw */
        }
    }
    if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(safe, { timeout: IDLE_TIMEOUT_MS })
        return
    }
    const PROBE_MS = 50
    const deadline = Date.now() + IDLE_TIMEOUT_MS
    let due = Date.now() + PROBE_MS
    const probe = () => {
        const now = Date.now()
        if (now - due < 10 || now >= deadline) return safe()
        due = now + PROBE_MS
        setTimeout(probe, PROBE_MS)
    }
    setTimeout(probe, PROBE_MS)
}

/** One passive capture listener per interaction type, removed on the first event; queued callbacks run in an idle slot. */
function armInteraction() {
    if (interactionArmed) return
    interactionArmed = true
    const fire = () => {
        if (interacted) return
        interacted = true
        for (const type of INTERACTION_EVENTS) window.removeEventListener(type, fire, LISTEN_OPTS)
        const pending = afterInteraction.splice(0)
        if (pending.length)
            whenIdle(() =>
                pending.forEach((fn) => {
                    try {
                        fn()
                    } catch {
                        /* one callback never stops the others */
                    }
                })
            )
    }
    for (const type of INTERACTION_EVENTS) window.addEventListener(type, fire, LISTEN_OPTS)
}

/** Runs `fn` in an idle slot after the visitor's first interaction (at once, in an idle slot, if it already happened). */
function afterFirstInteraction(fn: () => void) {
    if (interacted) {
        whenIdle(fn)
        return
    }
    afterInteraction.push(fn)
    armInteraction()
}

/** Landing params remembered for this tab session (empty when unknown). */
function landingProps(): Record<string, string> {
    try {
        const raw = sessionStorage.getItem(LANDING_KEY)
        const parsed = raw ? JSON.parse(raw) : null
        return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {}
    } catch {
        return {}
    }
}

/** True when this tab session landed from a paid-ad click (a click id only ad clicks carry). */
export function isPaidLanding(props: Record<string, unknown>): boolean {
    return PAID_CLICK_IDS.some((key) => typeof props[key] === "string" && (props[key] as string).trim() !== "")
}

/** Beacon body for /api/t/touch: landing URL, referrer and every query param (lower-cased keys, 300 chars each). */
export function buildTouchBody(input: {
    lmid: string | null
    landingHref: string
    referrer: string
    webdriver: boolean
    tz: string | null
    lang: string | null
    screen: string | null
}): string | null {
    if (!input.lmid || !ID_RE.test(input.lmid)) return null
    // Fragment and secret params (magic-link / OAuth tokens) never reach visitor_touches.
    const url = cleanLandingUrl(input.landingHref)
    if (!url) return null
    const params: Record<string, string> = {}
    let count = 0
    url.searchParams.forEach((value, rawKey) => {
        const key = rawKey.toLowerCase().slice(0, 64)
        if (!key || !value || key in params || count >= TOUCH_MAX_PARAMS) return
        params[key] = value.slice(0, 300)
        count++
    })
    return JSON.stringify({
        lmid: input.lmid,
        landing_url: url.href.slice(0, 2000),
        referrer: (input.referrer || "").slice(0, 1000),
        params,
        webdriver: input.webdriver,
        tz: input.tz ? input.tz.slice(0, 64) : null,
        lang: input.lang ? input.lang.slice(0, 35) : null,
        screen: input.screen,
    })
}

/** Sends this landing's one touch beacon (never awaited, never throws). */
function sendTouch(landingHref: string, onceKey: string) {
    try {
        if (!phOnce(onceKey)) return
        // A param-less page load later in this tab sends nothing more.
        if (onceKey !== TOUCH_ONCE) phOnce(TOUCH_ONCE)
        let tz: string | null = null
        try {
            tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null
        } catch {
            /* ignore */
        }
        const body = buildTouchBody({
            lmid: getLmid(),
            landingHref,
            referrer: document.referrer,
            webdriver: navigator.webdriver === true,
            tz,
            lang: navigator.language || null,
            screen: typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : null,
        })
        if (!body) return
        if (typeof navigator.sendBeacon === "function" && navigator.sendBeacon(TOUCH_URL, body)) return
        fetch(TOUCH_URL, { method: "POST", body, keepalive: true, credentials: "same-origin" }).catch(() => {})
    } catch {
        /* never throw */
    }
}

/**
 * Arms the touch beacon: it goes once per landing attribution in the tab session (touchOnceKey: a param-less landing
 * only when no touch went yet; an ad click after an organic visit sends its own), in an idle slot after the first
 * interaction, or at once when the page is hidden first (a visitor who leaves without touching the page still counts).
 */
function armTouch(landingHref: string) {
    const onceKey = touchOnceKey(landingHref)
    if (onceDone(onceKey)) return
    let sent = false
    const send = () => {
        if (sent) return
        sent = true
        document.removeEventListener("visibilitychange", onHidden)
        window.removeEventListener("pagehide", send)
        sendTouch(landingHref, onceKey)
    }
    const onHidden = () => {
        if (document.visibilityState === "hidden") send()
    }
    document.addEventListener("visibilitychange", onHidden)
    window.addEventListener("pagehide", send)
    afterFirstInteraction(send)
}

function run(fn: (p: PostHog) => void) {
    if (state === "off" || typeof window === "undefined") return
    if (ph) {
        try {
            fn(ph)
        } catch {
            /* never throw into the caller */
        }
        return
    }
    if (queue.length < MAX_QUEUE) queue.push(fn)
}

function startRecording(p: PostHog) {
    try {
        sessionStorage.setItem(RECORDING_KEY, "1")
    } catch {
        /* ignore */
    }
    if (!p.sessionRecordingStarted()) p.startSessionRecording({ event_trigger: true })
}

/**
 * Paid-ad session: start replay after the first interaction (idle slot), whatever the project's trigger settings
 * (event or URL triggers, match any or all). Sampling is overridden only on the first start: on a recorder that is
 * already running that override would force an extra full snapshot. No `_lm_rec` flag: each full page load of the
 * session starts again after its own first interaction, so the recorder never loads before one. Calls are a no-op when
 * replay is off in the project settings; card and password fields stay blocked (BLOCK_SELECTOR).
 */
function startPaidRecording(p: PostHog, tries = 0) {
    // posthog.init builds its replay module in a time-sliced task queue; an override sent before it exists is lost.
    if (!p.sessionRecording) {
        if (tries < 20) setTimeout(() => run((ready) => startPaidRecording(ready, tries + 1)), 250)
        return
    }
    const triggers = { url_trigger: true, event_trigger: true } as const
    p.startSessionRecording(p.sessionRecordingStarted() ? triggers : { ...triggers, sampling: true })
}

export function phCapture(event: string, props?: Record<string, unknown>): void {
    run((p) => {
        if (RECORDING_EVENTS.has(event)) startRecording(p)
        p.capture(event, props)
    })
}

/** field_typed, once per field per session. Handle: the typed username; email: domain only; others: no value. */
export function phFieldTyped(field: "handle" | "email" | "name" | "other", raw: string | null | undefined): void {
    const value = (raw ?? "").trim()
    if (!value || !phOnce(`field:${field}`)) return
    const props: Record<string, unknown> = { field }
    if (field === "handle") props.value = value.replace(/^@+/, "").slice(0, 100)
    if (field === "email") {
        const at = value.lastIndexOf("@")
        if (at > 0 && at < value.length - 1) props.domain = value.slice(at + 1).toLowerCase().slice(0, 100)
    }
    phCapture("field_typed", props)
}

export function phIdentify(userId: string | null | undefined, traits: { email?: string | null; name?: string | null; handle?: string | null }): void {
    if (!userId) return
    const clean: Record<string, string> = {}
    for (const [k, v] of Object.entries(traits)) if (typeof v === "string" && v.trim()) clean[k] = v.trim()
    const key = `${userId}|${JSON.stringify(clean)}`
    if (key === lastIdentify) return
    lastIdentify = key
    run((p) => p.identify(userId, clean))
}

function loadPosthog(landingHref: string) {
    if (state !== "waiting") return
    state = "loading"
    const lmid = getLmid()
    import("posthog-js")
        .then(({ default: posthog }) => {
            let resumeRecording = false
            try {
                resumeRecording = sessionStorage.getItem(RECORDING_KEY) === "1"
            } catch {
                /* ignore */
            }
            posthog.init(PH_TOKEN, {
                api_host: "/_ev",
                ui_host: "https://us.posthog.com",
                persistence: "localStorage",
                person_profiles: "identified_only",
                capture_pageview: "history_change",
                capture_pageleave: false,
                autocapture: false,
                capture_heatmaps: false,
                capture_dead_clicks: false,
                capture_exceptions: false,
                capture_performance: { web_vitals: false },
                disable_surveys: true,
                disable_product_tours: true,
                disable_web_experiments: true,
                disable_conversations: true,
                advanced_disable_feature_flags_on_first_load: true,
                // Replay starts on checkout_started / pay_pressed (startRecording; resumes after a reload in that tab) and, in a
                // paid-ad session, after the first interaction (startPaidRecording). Never loaded at init otherwise.
                disable_session_recording: !resumeRecording,
                bootstrap: lmid ? { distinctID: lmid } : undefined,
                before_send: (event) => (excludedContext() ? null : event),
                session_recording: {
                    maskAllInputs: false,
                    maskInputOptions: { password: true },
                    blockSelector: BLOCK_SELECTOR,
                },
            })
            ph = posthog
            state = "ready"
            try {
                // Keep `_lmid` equal to PostHog's anonymous id if PostHog already had one from an earlier visit.
                const id = posthog.get_distinct_id()
                if (posthog.get_property("$user_state") !== "identified" && id && ID_RE.test(id) && id !== lmid) writeCookie(LMID_COOKIE, id)
                const raw = sessionStorage.getItem(LANDING_KEY)
                if (raw) posthog.register_for_session(JSON.parse(raw))
            } catch {
                /* ignore */
            }
            const pending = queue.splice(0)
            for (const fn of pending) run(fn)
            try {
                // This page load's own URL first (an ad click after an organic landing in the same tab), else the session's landing.
                if (isPaidLanding(landingRecord(landingHref) || {}) || isPaidLanding(landingProps()))
                    afterFirstInteraction(() => run((p) => startPaidRecording(p)))
            } catch {
                /* ignore */
            }
        })
        .catch(() => {
            state = "off"
            queue.length = 0
        })
}

/**
 * Called by PostHogLoader after window load + idle time (the loader imports this module only then,
 * so neither this module nor posthog-js is in any page's initial bundle). `landingHref` is the URL
 * the page opened with, read by the loader at mount.
 */
export function startPosthog(landingHref?: string): void {
    if (typeof window === "undefined" || state !== "idle") return
    try {
        if (landingHref && new URL(landingHref).searchParams.get("qa") === "1") sessionStorage.setItem(DEV_QA_KEY, "1")
    } catch {
        /* ignore */
    }
    try {
        if (excludedContext() || !devAllowed()) {
            state = "off"
            queue.length = 0
            return
        }
        state = "waiting"
        const href = landingHref || location.href
        rememberLanding(href)
        armInteraction()
        armTouch(href)
        loadPosthog(href)
    } catch {
        state = "off"
    }
}
